import { Modal } from 'core/components/ui/Modal'
import { authApiDatasource } from 'features/auth/data/datasources/AuthApiDatasource'
import React, { useState } from 'react'
import { User } from '../../domain/entities/User'

interface ResetPasswordModalProps {
  isOpen: boolean
  onClose: () => void
  user: User | null
}

export const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ isOpen, onClose, user }) => {
  const [newPassword, setNewPassword] = useState('')
  const [updating, setUpdating] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [successMsg, setSuccessMsg] = useState('')

  const handleReset = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    setErrorMsg('')
    setSuccessMsg('')

    if (newPassword.length < 6) {
      setErrorMsg('Password must be at least 6 characters.')
      return
    }

    if (!user?.email) {
      setErrorMsg('User does not have an email address.')
      return
    }

    setUpdating(true)
    try {
      await authApiDatasource.resetPassword(user.email, newPassword)
      setSuccessMsg('Password reset successfully!')
      setTimeout(() => {
        onClose()
        setNewPassword('')
        setSuccessMsg('')
      }, 1500)
    } catch (err: unknown) {
      const errorVal = err as Error
      setErrorMsg(errorVal.message || 'Failed to reset password.')
    } finally {
      setUpdating(false)
    }
  }

  const handleClose = () => {
    setNewPassword('')
    setErrorMsg('')
    setSuccessMsg('')
    onClose()
  }

  const footer = (
    <>
      <button
        type="button"
        onClick={handleClose}
        disabled={updating}
        className="px-4 py-2 text-sm font-semibold border border-border rounded-btn bg-surface hover:bg-bg text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="reset-password-form"
        disabled={updating || !newPassword}
        className="px-4 py-2 text-sm font-semibold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn transition-colors disabled:opacity-50 min-w-[5rem]"
      >
        {updating ? 'Saving...' : 'Save Password'}
      </button>
    </>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Reset Password for ${user?.name || 'User'}`}
      footer={footer}
      size="sm"
    >
      <form id="reset-password-form" onSubmit={handleReset} className="space-y-4">
        {errorMsg && (
          <div className="p-3 text-xs bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium">
            {errorMsg}
          </div>
        )}
        {successMsg && (
          <div className="p-3 text-xs bg-status-active/10 border border-status-active/20 text-status-active rounded-btn font-medium">
            {successMsg}
          </div>
        )}

        <p className="text-xs text-text-secondary">
          Enter a new password for <strong>{user?.email}</strong>. They will use this password to sign in immediately.
        </p>

        <div className="space-y-1.5">
          <label className="text-xs font-bold text-text-primary uppercase tracking-wider">New Password</label>
          <input
            type="text"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full px-3.5 py-2 border border-border rounded-btn bg-bg text-text-primary text-sm focus:border-primary focus:outline-none transition-colors"
            placeholder="Min 6 characters"
          />
        </div>
      </form>
    </Modal>
  )
}
